import { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  Input,
  Select,
  Text,
  useToast,
} from "@chakra-ui/react";
import { crm } from "../../../theme";
import { attachToDossier, DossierInfo, DossierInstruction, getDossier } from "../../../services/govRegistrationService";

type Source = { documentId?: string; file?: File };

/** Anexos do dossiê da DUIMP (e-Docex): um documento do processo (ou upload) por documento de instrução. */
const DossierAttachPanel = ({ processId }: { processId: string }) => {
  const toast = useToast();
  const [info, setInfo] = useState<DossierInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [sources, setSources] = useState<Record<number, Source>>({});
  const [values, setValues] = useState<Record<number, string>>({});
  const [confirm, setConfirm] = useState<DossierInstruction | null>(null);
  const [sending, setSending] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await getDossier(processId);
      setInfo(d);
      setSources((prev) => {
        const next = { ...prev };
        d.instructions.forEach((i) => {
          if (!next[i.typeId] && i.suggestedDocumentId) next[i.typeId] = { documentId: i.suggestedDocumentId };
        });
        return next;
      });
    } catch {
      toast({ title: "Falha ao consultar o dossiê", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [processId]);

  const keywordValue = (i: DossierInstruction) => values[i.typeId] ?? i.keywords[0]?.value ?? "";
  const ready = (i: DossierInstruction) => {
    const s = sources[i.typeId];
    return !!info?.canAttach && !!(s?.file || s?.documentId) && keywordValue(i).trim() !== "";
  };

  const send = async () => {
    const i = confirm;
    setConfirm(null);
    if (!i) return;
    const s = sources[i.typeId] ?? {};
    const keywords = i.keywords.map((k, idx) => (idx === 0 ? { ...k, value: keywordValue(i).trim() } : k));
    setSending(true);
    try {
      const r = await attachToDossier(processId, { typeId: i.typeId, keywords, documentId: s.documentId, file: s.file });
      toast({
        title: r.success ? "Anexado ao dossiê" : "Não anexado",
        description: r.messages.join("\n"),
        status: r.success ? "success" : "error",
        duration: 12000,
        isClosable: true,
      });
      if (r.success) await load();
    } catch (e: any) {
      const data = e?.response?.data;
      const text = typeof data === "string" && data.trimStart().startsWith("<") ? undefined : data?.messages?.join?.("\n");
      toast({
        title: "Erro ao anexar",
        description: text ?? (e?.response?.status ? `Servidor respondeu ${e.response.status}.` : "Sem resposta do servidor."),
        status: "error",
        duration: 12000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  if (!info) return loading ? <Text mt="16px" fontSize="13px" color={crm.textMuted}>Consultando dossiê…</Text> : null;

  return (
    <Box px={{ base: "16px", md: "24px" }} pb="16px" pt="16px" borderTop={`1px solid ${crm.border}`}>
      <Flex align="center" justify="space-between" mb="8px" gap="8px" wrap="wrap">
        <Text fontWeight="600" fontSize="14px" color={crm.text}>
          Dossiê da DUIMP {info.duimpNumber ? `· ${info.duimpNumber}` : ""}
        </Text>
        <Button size="xs" variant="outline" onClick={load} isLoading={loading}>
          Atualizar
        </Button>
      </Flex>
      {info.message && (
        <Text fontSize="13px" color={crm.textMuted} mb="8px">
          {info.message}
        </Text>
      )}
      {info.instructions.length === 0 && (
        <Text fontSize="13px" color={crm.textMuted}>
          Nenhum documento de instrução configurado (Siscomex:InstructionDocuments).
        </Text>
      )}
      {info.instructions.map((i) => (
        <Box key={i.typeId} p="12px" mb="8px" border={`1px solid ${crm.border}`} borderRadius="8px">
          <Flex align="center" gap="8px" wrap="wrap" mb="8px">
            <Text fontWeight="600" fontSize="13px" color={crm.text}>
              {i.typeName}
            </Text>
            <Badge colorScheme={i.attached ? "green" : "orange"}>{i.attached ? "anexado" : "pendente"}</Badge>
            {i.attachment && (
              <Text fontSize="12px" color={crm.textMuted}>
                {i.attachment.fileName} · {new Date(i.attachment.attachedAt).toLocaleString("pt-BR")} · {i.attachment.attachedBy}
              </Text>
            )}
          </Flex>
          <Flex gap="8px" wrap="wrap" align="center">
            <Select
              size="sm"
              maxW="280px"
              placeholder="Documento do processo"
              value={sources[i.typeId]?.file ? "" : sources[i.typeId]?.documentId ?? ""}
              onChange={(e) => setSources({ ...sources, [i.typeId]: { documentId: e.target.value || undefined } })}
            >
              {info.storedDocuments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
            <Input
              size="sm"
              type="file"
              maxW="240px"
              accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.doc,.docx,.xls,.xlsx,.txt,.xml"
              onChange={(e) => {
                const file = e.target.files?.[0];
                setSources({ ...sources, [i.typeId]: file ? { file } : {} });
              }}
            />
            <Input
              size="sm"
              maxW="200px"
              placeholder="Número (palavra-chave 1)"
              value={keywordValue(i)}
              onChange={(e) => setValues({ ...values, [i.typeId]: e.target.value })}
            />
            <Button size="sm" colorScheme="blue" isDisabled={!ready(i) || sending} onClick={() => setConfirm(i)}>
              Anexar ao dossiê
            </Button>
          </Flex>
        </Box>
      ))}

      <Text fontSize="12px" fontWeight="600" color={crm.textMuted} mt="12px" mb="4px">
        Documentos no Portal Único
      </Text>
      {info.portalError && (
        <Text fontSize="12px" color={crm.danger}>
          {info.portalError}
        </Text>
      )}
      {info.portalOk && info.portalDocuments.length === 0 && (
        <Text fontSize="12px" color={crm.textFaint}>
          Nenhum documento anexado no Portal.
        </Text>
      )}
      {info.portalDocuments.map((d) => (
        <Text key={d.id} fontSize="12px" color={crm.text}>
          {d.typeName || d.typeId} · {d.fileName}
          {d.keywords.length > 0 ? ` · ${d.keywords.join(", ")}` : ""}
        </Text>
      ))}

      <AlertDialog isOpen={confirm !== null} leastDestructiveRef={cancelRef} onClose={() => setConfirm(null)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Enviar ao Portal Único?</AlertDialogHeader>
            <AlertDialogBody>
              Anexar <b>{confirm?.typeName}</b> ao dossiê da DUIMP <b>{info.duimpNumber}</b> no ambiente ({info.environment}). O envio fica
              registrado no histórico com o seu usuário.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirm(null)}>
                Cancelar
              </Button>
              <Button colorScheme="blue" ml={3} onClick={send}>
                Enviar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default DossierAttachPanel;
